import { Card, StatusChip } from '../../components/ui';
import { Clock, Check, AlertCircle, FileText, RefreshCw } from 'lucide-react';
import type { Resume, ResumeJobStatus } from '../../types';

interface ResumeParseStatusProps {
  resume: Resume | null;
  isPolling?: boolean;
  onRetry?: () => void;
  className?: string;
}

export function ResumeParseStatus({ 
  resume, 
  isPolling = false, 
  onRetry,
  className = "" 
}: ResumeParseStatusProps) {
  if (!resume) {
    return (
      <Card className={className}>
        <div className="space-y-4">
          <h3 className="font-semibold text-foreground">Parse Status</h3>
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Waiting for upload...</span>
          </div>
        </div>
      </Card>
    );
  }

  const parseStatus = resume.status.parse_status || 'pending';
  
  const getStatusConfig = (status: ResumeJobStatus) => {
    switch (status) {
      case 'pending':
        return {
          icon: Clock,
          text: 'Queued for processing',
          color: 'text-yellow-500',
          bgColor: 'bg-yellow-500/10',
          chipVariant: 'default' as const,
          chipColor: 'yellow' as const
        };
      case 'processing':
        return {
          icon: RefreshCw,
          text: 'Extracting text...',
          color: 'text-blue-500',
          bgColor: 'bg-blue-500/10',
          chipVariant: 'default' as const,
          chipColor: 'blue' as const,
          animate: true
        };
      case 'completed':
        return {
          icon: Check,
          text: 'Text extraction complete',
          color: 'text-green-500',
          bgColor: 'bg-green-500/10',
          chipVariant: 'default' as const,
          chipColor: 'green' as const
        };
      case 'failed':
        return {
          icon: AlertCircle,
          text: 'Extraction failed',
          color: 'text-red-500',
          bgColor: 'bg-red-500/10',
          chipVariant: 'default' as const,
          chipColor: 'red' as const
        };
      case 'retrying':
        return {
          icon: RefreshCw,
          text: 'Retrying extraction...',
          color: 'text-orange-500',
          bgColor: 'bg-orange-500/10',
          chipVariant: 'default' as const,
          chipColor: 'amber' as const,
          animate: true
        };
      default:
        return {
          icon: Clock,
          text: 'Unknown status',
          color: 'text-muted-foreground',
          bgColor: 'bg-muted/10',
          chipVariant: 'default' as const,
          chipColor: 'gray' as const
        };
    }
  };

  const config = getStatusConfig(parseStatus);
  const IconComponent = config.icon;

  return (
    <Card className={className}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground">Parse Status</h3>
          {isPolling && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              Live updates
            </div>
          )}
        </div>

        {/* Main Status */}
        <div className={`flex items-center gap-3 p-3 rounded-lg ${config.bgColor}`}>
          <IconComponent 
            className={`h-5 w-5 ${config.color} ${config.animate ? 'animate-spin' : ''}`} 
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">{config.text}</span>
              <StatusChip status={parseStatus} variant={config.chipVariant} color={config.chipColor}>
                {parseStatus}
              </StatusChip>
            </div>
            
            {/* Additional details */}
            {resume.page_count && (
              <div className="text-xs text-muted-foreground mt-1">
                {resume.page_count} page{resume.page_count !== 1 ? 's' : ''} • {(resume.file_size / 1024 / 1024).toFixed(1)} MB
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {parseStatus === 'failed' && resume.status.last_error && (
          <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <div className="font-medium text-red-700 dark:text-red-400">Error Details:</div>
                <div className="text-red-600 dark:text-red-300 mt-1 font-mono text-xs">
                  {resume.status.last_error}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Processing Progress */}
        {(parseStatus === 'processing' || parseStatus === 'retrying') && (
          <div className="space-y-2">
            <div className="w-full bg-muted rounded-full h-2">
              <div className="bg-primary h-2 rounded-full animate-pulse transition-all duration-1000" 
                   style={{ width: parseStatus === 'processing' ? '70%' : '30%' }} />
            </div>
            <div className="text-xs text-muted-foreground text-center">
              {parseStatus === 'processing' ? 'Extracting text from document...' : 'Attempting retry...'}
            </div>
          </div>
        )}

        {/* File Info */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <FileText className="h-4 w-4" />
          <span>{resume.original_filename}</span>
          <span>•</span>
          <span>Uploaded {new Date(resume.upload_date).toLocaleString()}</span>
        </div>

        {/* Retry Button */}
        {parseStatus === 'failed' && onRetry && (
          <button
            onClick={onRetry}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            <RefreshCw className="h-4 w-4" />
            Retry Parsing
          </button>
        )}
      </div>
    </Card>
  );
}